// ----------------------------------------------------------------
// Spiderfy — spreads overlapping cluster leaves into a small radial
// layout when they cannot be visually separated by zooming further
// (identical/near-identical coordinates, or already at max cluster
// zoom). Extracted from map-view.tsx so the offset/leaf-resolution
// math is independently unit-testable.
// ----------------------------------------------------------------

/** Pixel offset for one spiderfied leg, relative to the cluster's screen position. */
export interface SpiderfyLegOffset {
  dx: number;
  dy: number;
}

const DEFAULT_LEGS_PER_RING = 8;
const DEFAULT_BASE_RADIUS_PX = 42;
const DEFAULT_RING_SPACING_PX = 34;

/**
 * Lays out `count` leaf positions as pixel offsets (dx, dy) around a center
 * point, in concentric rings starting at the top (12 o'clock) going
 * clockwise. Rings hold up to `legsPerRing` positions before a new, wider
 * ring starts, keeping large clusters legible instead of one huge circle.
 */
export function computeSpiderfyLegOffsets(
  count: number,
  options: { legsPerRing?: number; baseRadiusPx?: number; ringSpacingPx?: number } = {},
): SpiderfyLegOffset[] {
  const legsPerRing = options.legsPerRing ?? DEFAULT_LEGS_PER_RING;
  const baseRadiusPx = options.baseRadiusPx ?? DEFAULT_BASE_RADIUS_PX;
  const ringSpacingPx = options.ringSpacingPx ?? DEFAULT_RING_SPACING_PX;

  return Array.from({ length: count }, (_, i) => {
    const ring = Math.floor(i / legsPerRing);
    const indexInRing = i % legsPerRing;
    const countInRing = Math.min(legsPerRing, count - ring * legsPerRing);
    const radius = baseRadiusPx + ring * ringSpacingPx;
    const angle = (2 * Math.PI * indexInRing) / countInRing - Math.PI / 2;
    return { dx: radius * Math.cos(angle), dy: radius * Math.sin(angle) };
  });
}

/** A screen-space pixel point, e.g. the return value of `map.project()`. */
export interface PixelPoint {
  x: number;
  y: number;
}

/**
 * Adds a spiderfy leg's pixel offset to a screen-space point. Pure arithmetic
 * only — callers resolve the actual lat/lng via the map instance's own
 * `map.project(center)` / `map.unproject([x, y])`, which correctly accounts
 * for the map's current bearing, pitch, and projection (unlike a flat-earth
 * meters-per-pixel approximation, which drifts under rotation/tilt).
 */
export function applyPixelOffset(point: PixelPoint, offset: SpiderfyLegOffset): PixelPoint {
  return { x: point.x + offset.dx, y: point.y + offset.dy };
}

/**
 * Decides whether a cluster click should spiderfy (spread leaves radially)
 * instead of the normal "zoom into the cluster" behavior. Spiderfy is needed
 * when zooming further would not visually separate the cluster:
 * - `expansionZoom` is missing/non-finite (lookup failed on the worker), or
 * - `expansionZoom` exceeds the source's `clusterMaxZoom` (supercluster's
 *   signal that the leaves are at/near-identical coordinates and never split
 *   before maxZoom), or
 * - `expansionZoom` would not advance past the current zoom (already there).
 */
export function shouldSpiderfyCluster(
  expansionZoom: number | null | undefined,
  currentZoom: number,
  clusterMaxZoom: number,
): boolean {
  if (expansionZoom == null || !Number.isFinite(expansionZoom)) return true;
  if (expansionZoom > clusterMaxZoom) return true;
  return expansionZoom <= currentZoom;
}
